/**
 * Normalize plain-text EAD node reports into markdown with aligned steps.
 * Shared by chat and PFM node report panel renderers.
 */

function trimLine(line: string): string {
  return String(line || "").replace(/\r/g, "").trim();
}

/** True when line is a step action (not Expected Result / Evidence). */
function parseStepLine(compact: string): { num: string; action: string } | null {
  const m = compact.match(/^step\s+(\d+)\s*:\s*(.+)$/i);
  if (!m) return null;
  return { num: m[1], action: m[2].trim() };
}

function parseSubStepField(
  compact: string,
): { label: string; value: string } | null {
  const bare = compact.match(
    /^(Expected Result|Evidence Image|Evidence)\s*:\s*(.*)$/i,
  );
  if (bare) {
    const label = bare[1].toLowerCase().startsWith("evidence") ? "Evidence" : "Expected Result";
    return { label, value: bare[2].trim() };
  }
  const bulleted = compact.match(
    /^[-*+]\s*(Expected Result|Evidence Image|Evidence)\s*:\s*(.*)$/i,
  );
  if (bulleted) {
    const label = bulleted[1].toLowerCase().startsWith("evidence") ? "Evidence" : "Expected Result";
    return { label, value: bulleted[2].trim() };
  }
  return null;
}

/**
 * Convert label-style reports (Feature / Test Case / Step blocks) into markdown
 * with numbered steps and nested Expected Result / Evidence lines.
 */
export function formatEadReportMarkdownForDisplay(raw: string): string {
  const input = String(raw || "").replace(/\r\n?/g, "\n");
  if (!input.trim()) return "";

  const lines = input.split("\n");
  const out: string[] = [];
  let withinSteps = false;
  let stepsEmitted = 0;

  const flushBlank = (): void => {
    if (out.length === 0 || out[out.length - 1] === "") return;
    out.push("");
  };

  for (const rawLine of lines) {
    const compact = trimLine(rawLine);
    if (!compact) {
      flushBlank();
      continue;
    }

    if (/^feature\s+[a-z0-9._-]+\s*:/i.test(compact)) {
      flushBlank();
      out.push(`### ${compact}`);
      withinSteps = false;
      stepsEmitted = 0;
      continue;
    }
    if (/^test\s*case\s+[a-z0-9._-]+\s*:/i.test(compact)) {
      flushBlank();
      out.push(`### ${compact}`);
      withinSteps = false;
      stepsEmitted = 0;
      continue;
    }
    if (/^steps?\s*:\s*$/i.test(compact)) {
      flushBlank();
      out.push("#### Steps");
      withinSteps = true;
      stepsEmitted = 0;
      continue;
    }

    const step = parseStepLine(compact);
    if (step) {
      if (stepsEmitted > 0) flushBlank();
      out.push(`${step.num}. ${step.action}`);
      withinSteps = true;
      stepsEmitted += 1;
      continue;
    }

    const sub = parseSubStepField(compact);
    if (sub && withinSteps) {
      out.push(`   - **${sub.label}:** ${sub.value || "NONE"}`);
      continue;
    }

    const fieldMatch = compact.match(
      /^(Objective|Description|Preconditions?|Pre-conditions?|Test Data|In-Scope Behaviors?)\s*:\s*(.*)$/i,
    );
    if (fieldMatch) {
      out.push(`- **${fieldMatch[1]}:** ${fieldMatch[2].trim()}`);
      continue;
    }

    if (/^node\s+summary\s*:\s*$/i.test(compact)) {
      flushBlank();
      out.push("## Node Summary");
      withinSteps = false;
      continue;
    }
    if (/^features\s*:\s*$/i.test(compact)) {
      flushBlank();
      out.push("## Features");
      withinSteps = false;
      continue;
    }

    out.push(compact);
    if (!sub) withinSteps = false;
  }

  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
